import { useEffect, useRef } from 'react';
import CheckReaderDevice from '../devices/check-reader/CheckReaderDevice';
import { DeviceStatusFlags } from '../devices/MonitoredDevice';
import logger from '../helpers/logger';
import { useStore } from './useStore';

/** How long the insert-check screen waits for a check before showing the session countdown. */
export const CHECK_INSERT_WINDOW_MS = 30000;
/** How often the KioWare inactivity timer is reset while the window is open. */
export const CHECK_INSERT_KEEPALIVE_MS = 1000;
/** Delay before re-arming the scanner after an empty or invalid read. */
export const CHECK_INSERT_REARM_DELAY_MS = 1000;
/** ignoreCheckSensor mode: delay between the device-reset cancelRead() and the first read. */
export const CHECK_INSERT_IGNORE_SENSOR_ARM_DELAY_MS = 500;

export interface CheckInsertRead {
	imagePath: string;
	micr: string;
	account: string;
	routing: string;
}

export interface UseCheckInsertSessionOptions {
	/** True only while the screen is in AWAITING_INSERT for the scanner and the feature flag is on. */
	enabled: boolean;
	checkReader: CheckReaderDevice | null;
	stampVoid: boolean;
	ignoreCheckSensor: boolean;
	/** Return false when the screen stays on the insert step. Anything else means the check was accepted; a throw counts as false. */
	onCheckRead: (read: CheckInsertRead) => boolean | void;
	onDeviceError: (err: number) => void;
}

/**
 * Owns the insert-check step (#8844). Keeps the scanner armed across empty or invalid reads,
 * gives the customer a 30 s window (KioWare kept alive every second), then shows the session
 * countdown. A failed attempt, or the customer continuing at the countdown, starts a new window.
 */
export function useCheckInsertSession({
	enabled,
	checkReader,
	stampVoid,
	ignoreCheckSensor,
	onCheckRead,
	onDeviceError,
}: UseCheckInsertSessionOptions) {
	const { sessionStore } = useStore();

	const onCheckReadRef = useRef(onCheckRead);
	onCheckReadRef.current = onCheckRead;
	const onDeviceErrorRef = useRef(onDeviceError);
	onDeviceErrorRef.current = onDeviceError;

	useEffect(() => {
		if (!enabled || !checkReader) return;

		const reader = checkReader;
		let aborted = false;
		let armTimeout: ReturnType<typeof setTimeout> | null = null;
		let windowTimeout: ReturnType<typeof setTimeout> | null = null;
		let keepAliveInterval: ReturnType<typeof setInterval> | null = null;
		let isCountdownShown = false;
		// Set once the countdown decides to end the visit; nothing may restart idle monitoring after that.
		let expired = false;

		// KioWare calls can throw; a failed cancel must not stop the rest of the teardown.
		const cancelRead = () => {
			try {
				reader.cancelRead();
			} catch (error) {
				logger.logGeneral.error({}, '[useCheckInsertSession] cancelling the check read failed', error);
			}
		};

		const stopWindow = () => {
			if (windowTimeout) clearTimeout(windowTimeout);
			if (keepAliveInterval) clearInterval(keepAliveInterval);
			windowTimeout = null;
			keepAliveInterval = null;
		};

		const closeCountdown = () => {
			if (!isCountdownShown) return;
			isCountdownShown = false;
			if (sessionStore.countdownSeconds !== null) {
				sessionStore.dismissCountdown();
				sessionStore.startCountdownTriggerMonitoring();
			}
		};

		const startWindow = () => {
			stopWindow();
			sessionStore.setSkipCountdownModal(true);
			sessionStore.setTimeoutNavigationCallback(null);
			sessionStore.beginKioWareSession();
			// With the modal skipped, a countdown that triggers still ends the session, so keep KioWare's
			// remaining time away from the threshold for the whole window.
			keepAliveInterval = setInterval(() => sessionStore.beginKioWareSession(), CHECK_INSERT_KEEPALIVE_MS);
			windowTimeout = setTimeout(() => {
				stopWindow();
				sessionStore.setSkipCountdownModal(false);
				logger.logGeneral.log({}, '[useCheckInsertSession] no check read within the insert window, showing the session countdown');
				isCountdownShown = sessionStore.showCountdownNow(
					() => {
						isCountdownShown = false;
						if (!aborted) startWindow();
					},
					() => {
						aborted = true;
						expired = true;
						isCountdownShown = false;
						stopWindow();
						if (armTimeout) clearTimeout(armTimeout);
						cancelRead();
					}
				);
			}, CHECK_INSERT_WINDOW_MS);
		};

		function handleRead(err: number, imagePath: string, micr: string, account: string, routing: string) {
			if (aborted) return;
			closeCountdown();

			if (err === DeviceStatusFlags.Ok) {
				sessionStore.beginKioWareSession();
				let accepted: boolean;
				try {
					accepted = onCheckReadRef.current({ imagePath, micr, account, routing }) !== false;
				} catch (error) {
					// The scanner is already off; treat it as not taken so the customer can insert again.
					logger.logGeneral.error({}, '[useCheckInsertSession] the screen failed to take the check read, re-arming', error);
					accepted = false;
				}
				if (!accepted) {
					sessionStore.setSkipCountdownModal(false);
					cancelRead();
					startWindow();
					if (armTimeout) clearTimeout(armTimeout);
					armTimeout = setTimeout(arm, CHECK_INSERT_REARM_DELAY_MS);
					return;
				}
				stopWindow();
				return;
			}

			if (err === DeviceStatusFlags.RecoverError) {
				// KioWare reports an empty capture (slow insert) as success; PertechCheckReader maps it to RecoverError.
				logger.logGeneral.warn({}, `[useCheckInsertSession] empty or invalid check read (err ${err}), re-arming the scanner`);
				cancelRead();
				startWindow();
				if (armTimeout) clearTimeout(armTimeout);
				armTimeout = setTimeout(arm, CHECK_INSERT_REARM_DELAY_MS);
				return;
			}

			stopWindow();
			onDeviceErrorRef.current(err);
		}

		function arm() {
			armTimeout = null;
			if (aborted) return;
			try {
				reader.readCheck(stampVoid, handleRead);
			} catch (error) {
				// Without a stopped window the keep-alive would hold the kiosk open forever.
				logger.logGeneral.error({}, '[useCheckInsertSession] arming the check reader failed', error);
				stopWindow();
				sessionStore.setSkipCountdownModal(false);
				onDeviceErrorRef.current(DeviceStatusFlags.NoRecoverError);
			}
		}

		startWindow();
		if (ignoreCheckSensor) {
			cancelRead();
			armTimeout = setTimeout(arm, CHECK_INSERT_IGNORE_SENSOR_ARM_DELAY_MS);
		} else {
			arm();
		}

		return () => {
			aborted = true;
			stopWindow();
			if (armTimeout) clearTimeout(armTimeout);
			cancelRead();
			closeCountdown();
			sessionStore.setSkipCountdownModal(false);
			sessionStore.setTimeoutNavigationCallback(null);
			if (!expired) sessionStore.startCountdownTriggerMonitoring();
		};
	}, [enabled, checkReader, stampVoid, ignoreCheckSensor, sessionStore]);
}
